/**
 * A market as the Add Outcomes modal groups its outcomes under — `.m-mkt-d` in
 * the prototype's modals.css (CAE-1553).
 *
 * The head carries the checkbox that selects every outcome inside it, which is
 * what makes the modal a bulk picker rather than the one-at-a-time flow it
 * replaced.
 *
 * **Its outcomes are fetched when they are needed, not when the market is
 * drawn.** A real World Cup fixture carries 141 markets (verified live, see
 * `fixture-market-drilldown.tsx`), so a list that loaded every market's
 * outcomes to draw its heads would be 141 requests for one expanded fixture.
 *
 * > **Ticking a collapsed market selects what it turns out to carry.** The head
 * > checkbox has to answer for rows that have not been fetched yet, and the two
 * > obvious ways out are both wrong: disabling it until the market is expanded
 * > makes the operator open every market to bulk-select, which is the feature
 * > gone, and eagerly fetching to keep it enabled is the fan-out above. So the
 * > tick starts the fetch and is remembered — `onBulkSelect` is called again
 * > with the outcomes once they arrive. The modal owns that intent because it
 * > owns the selection; this component only reports it.
 *
 * > **No market-category pill.** `.m-mkt-d-cat` draws `MATCH RESULT` / `GOALS`
 * > beside the name in the prototype, and the feed sends no such field —
 * > `markets.type` is a copy of the market's own name, so a pill fed from it
 * > would print the name twice. Dropped for CAE-1589, which stays open to ask
 * > Caesars whether a category exists at all. Logged in
 * > `docs/design/admin-ui/prototype/README.md`.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import type { Theme } from '@mui/material/styles';
import {
  catalogueApi,
  type CatalogueLocale,
  type CatalogueMarket,
  type CatalogueOutcome,
} from '../../../../api/catalogue';
import { outcomesQueryKey } from '../../../../components/catalogue';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FixtureSelectCheckbox,
  RefreshIcon,
} from '../../../../components/ui';
import { marketPickLabel } from './add-outcomes-picks';
import { OutcomePickRow, OUTCOME_PICK_COLUMNS } from './outcome-pick-row';

/** The catalogue's own 60s staleness, so the modal and the drill-down agree. */
const CATALOGUE_STALE_MS = 60_000;

const ColumnHead = ({ children, align }: { children?: string; align?: 'right' }) => (
  <Box
    sx={(theme: Theme) => ({
      fontSize: '10.5px',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: theme.tokens.color.ink4,
      textAlign: align ?? 'left',
    })}
  >
    {children}
  </Box>
);

export interface MarketPickCardProps {
  market: CatalogueMarket;
  locale: CatalogueLocale;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Outcome external ids currently picked, across the whole modal. */
  selectedIds: ReadonlySet<string>;
  /** Outcome external ids already a Reel Option on the reel being added to. */
  onReelIds: ReadonlySet<string>;
  /** Hide the ones already on the reel rather than drawing them inert. */
  hideOnReel: boolean;
  onOutcomeToggle: (market: CatalogueMarket, outcome: CatalogueOutcome, selected: boolean) => void;
  /**
   * Every selectable outcome in this market, with whether to pick or drop them.
   * Fires on the head's tick, and again when a pending tick's outcomes arrive.
   *
   * `loaded` says whether that list is the answer or merely what is known so
   * far. Without it an empty list is ambiguous — "the outcomes have not arrived"
   * and "there is nothing here to pick" are different states, and reading the
   * second as the first parks the market as a pending bulk select that never
   * resolves.
   */
  onBulkSelect: (
    market: CatalogueMarket,
    outcomes: CatalogueOutcome[],
    selected: boolean,
    loaded: boolean,
  ) => void;
  /** This market was ticked while collapsed, and its outcomes are still coming. */
  pendingBulkSelect: boolean;
}

export const MarketPickCard = ({
  market,
  locale,
  open,
  onOpenChange,
  selectedIds,
  onReelIds,
  hideOnReel,
  onOutcomeToggle,
  onBulkSelect,
  pendingBulkSelect,
}: MarketPickCardProps) => {
  const label = marketPickLabel(market);

  const outcomesQuery = useQuery({
    queryKey: outcomesQueryKey(market.externalId, locale),
    queryFn: () => catalogueApi.listOutcomesForMarket(market.externalId, locale),
    staleTime: CATALOGUE_STALE_MS,
    // Opened, or ticked while collapsed — either way the rows are now wanted.
    enabled: open || pendingBulkSelect,
  });

  const loaded = outcomesQuery.data;
  const failed = outcomesQuery.isError;

  /** Everything not already on the reel: what the rows offer, and what "picked" counts. */
  const offered = (loaded ?? []).filter((outcome) => !onReelIds.has(outcome.externalId));
  /*
   * What the head's checkbox answers for: the offered outcomes the feed has
   * not marked suspended (CAE-1675). A suspended outcome keeps its row and its
   * badge, and the Operator can still tick it by hand — but a tick on the head
   * must not sweep in a selection the book has taken off, which is how one
   * reached a reel with nobody having looked at it. Only an explicit
   * `active: false` is suspended; `null` is the catalogue declining to say.
   */
  const selectable = offered.filter((outcome) => outcome.active !== false);
  const suspended = offered.length - selectable.length;
  const visible = hideOnReel ? offered : (loaded ?? []);
  const pickedHere = offered.filter((outcome) => selectedIds.has(outcome.externalId)).length;
  const allPicked =
    selectable.length > 0 && selectable.every((outcome) => selectedIds.has(outcome.externalId));
  const somePicked = pickedHere > 0 && !allPicked;

  /*
   * The deferred half of a tick on a collapsed market. The query above was
   * switched on by the same tick; this applies it the moment the rows land.
   */
  /*
   * Deliberately keyed on the arrival alone.
   *
   * `selectable` is recomputed from `selectedIds` on every render, so listing it
   * here would re-fire the bulk select each time the operator unticked one of
   * the rows it had just ticked — putting it straight back. The effect wants to
   * run once, when the outcomes a pending tick asked for land, and the values it
   * closes over are that render's, which is what it needs.
   */
  useEffect(() => {
    if (pendingBulkSelect && loaded) onBulkSelect(market, selectable, true, true);
  }, [pendingBulkSelect, loaded]);

  /*
   * The same deferred tick when the request *fails*.
   *
   * `loaded` stays undefined on an error, so the effect above never fires and
   * the market would sit in `pendingBulk` for the rest of the session with its
   * query pinned on — the exact state the `loaded` flag was added to prevent,
   * reached by the other door. Clearing the intent releases it and re-arms the
   * head, so a second tick is a retry rather than a no-op.
   */
  useEffect(() => {
    if (pendingBulkSelect && failed) onBulkSelect(market, [], false, true);
  }, [pendingBulkSelect, failed]);

  return (
    <Box
      sx={(theme: Theme) => ({
        // The list above is a capped flex column, and a flex child shrinks to
        // fit — the same squash `fixture-market-drilldown.tsx` records.
        flexShrink: 0,
        backgroundColor: theme.tokens.color.surface,
        border: `${theme.tokens.border.width} solid ${theme.tokens.color.lineStrong}`,
        borderRadius: `${theme.tokens.radius.insetRow}px`,
        overflow: 'hidden',
        ...(open ? { boxShadow: theme.tokens.shadow.card } : {}),
      })}
    >
      <Box
        onClick={() => onOpenChange(!open)}
        sx={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px' }}
      >
        <FixtureSelectCheckbox
          /*
           * `pendingBulkSelect` counts as ticked. The outcomes a collapsed tick
           * asked for have not arrived, so `allPicked` is still false and the
           * box would render empty against the operator's own click — no
           * spinner, nothing moved. Worse, the controlled input is forced back
           * to unchecked every render, so the natural undo re-fires
           * `onChange(true)` and re-confirms the intent instead of clearing it.
           */
          checked={allPicked || pendingBulkSelect}
          indeterminate={somePicked}
          label={`Select all outcomes in ${label}`}
          /*
           * Nothing left to pick — every outcome is already a Reel Option, the
           * rest are suspended, or the feed sent none. A live-looking box that
           * does nothing is worse than one that says so, and the meta line
           * beside it explains which.
           */
          disabled={Boolean(loaded) && selectable.length === 0}
          // A tick adds what can be bulk-picked; an untick clears the whole
          // market, a suspended outcome ticked by hand included.
          onChange={(next) =>
            onBulkSelect(market, next ? selectable : offered, next, Boolean(loaded))
          }
        />

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Box
            component="span"
            sx={(theme: Theme) => ({
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '-0.005em',
              color: theme.tokens.color.ink,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            })}
          >
            {label}
          </Box>
          {/*
           * `.m-mkt-d-meta` reads "N outcomes · N picked" off mock data the
           * prototype has in hand. The catalogue answers a market at a time, so
           * the line appears when the figures do — zero outcomes is a reading an
           * operator acts on and must not be shown before it is true.
           */}
          {loaded ? (
            <Box
              sx={(theme: Theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                color: theme.tokens.color.ink3,
                fontVariantNumeric: 'tabular-nums',
              })}
            >
              <span>
                {loaded.length} {loaded.length === 1 ? 'outcome' : 'outcomes'}
              </span>
              {pickedHere > 0 ? (
                <>
                  <span>·</span>
                  <Box
                    component="span"
                    sx={(theme: Theme) => ({
                      color: theme.tokens.color.accent,
                      fontWeight: 600,
                    })}
                  >
                    {pickedHere} picked
                  </Box>
                </>
              ) : null}
              {suspended > 0 ? (
                <>
                  <span>·</span>
                  <Box
                    component="span"
                    sx={(theme: Theme) => ({ color: theme.tokens.color.warn, fontWeight: 500 })}
                  >
                    {suspended} suspended
                  </Box>
                </>
              ) : null}
              {loaded.length > 0 && offered.length === 0 ? (
                <>
                  <span>·</span>
                  <Box
                    component="span"
                    sx={(theme: Theme) => ({ color: theme.tokens.color.warn, fontWeight: 500 })}
                  >
                    All already on reel
                  </Box>
                </>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <IconButton
          size="small"
          aria-expanded={open}
          aria-label={open ? `Collapse outcomes for ${label}` : `Expand outcomes for ${label}`}
          onClick={(event) => {
            event.stopPropagation();
            onOpenChange(!open);
          }}
          sx={(theme: Theme) => ({
            width: '28px',
            height: '28px',
            borderRadius: `${theme.tokens.radius.control}px`,
            color: theme.tokens.color.ink4,
            '&:hover': { backgroundColor: theme.tokens.color.bg, color: theme.tokens.color.ink2 },
          })}
        >
          {open ? <ChevronDownIcon fontSize="inherit" /> : <ChevronRightIcon fontSize="inherit" />}
        </IconButton>
      </Box>

      {open ? (
        <Box
          sx={(theme: Theme) => ({
            borderTop: theme.tokens.border.default,
            backgroundColor: theme.tokens.color.surfaceMuted,
            // Four columns are what the row is for; below the breakpoint it
            // scrolls in its own container rather than shedding one.
            overflowX: 'auto',
          })}
        >
          {outcomesQuery.isLoading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', padding: '20px' }}>
              <CircularProgress size={18} aria-label={`Loading outcomes for ${label}`} />
            </Box>
          ) : failed ? (
            /*
             * A failed request is not a market with no outcomes. Drawn as one it
             * is four column heads over nothing, with no account of why and
             * nothing to do about it — so it says what happened and offers the
             * only action that can help.
             */
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '12px 14px',
              }}
            >
              <Box
                component="span"
                sx={(theme: Theme) => ({ fontSize: '13px', color: theme.tokens.color.warn })}
              >
                Couldn’t load outcomes for this market.
              </Box>
              <Button
                size="small"
                variant="text"
                startIcon={<RefreshIcon />}
                onClick={() => void outcomesQuery.refetch()}
              >
                Retry
              </Button>
            </Box>
          ) : visible.length === 0 ? (
            /*
             * Nothing to draw, and the column heads must not be drawn over it: a
             * bare header row reads as a table still filling. Which emptiness
             * this is the operator can act on — the hide toggle is theirs to
             * undo, an empty market is the feed's.
             */
            <Box
              sx={(theme: Theme) => ({
                padding: '12px 14px',
                fontSize: '13px',
                color: theme.tokens.color.ink3,
              })}
            >
              {(loaded ?? []).length === 0
                ? 'The feed sent no outcomes for this market.'
                : 'Every outcome here is already on the reel.'}
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: OUTCOME_PICK_COLUMNS,
                  gap: '12px',
                  padding: '10px 14px 6px',
                }}
              >
                <ColumnHead>Outcome</ColumnHead>
                <ColumnHead align="right">Decimal</ColumnHead>
                <ColumnHead align="right">Fractional</ColumnHead>
                <ColumnHead>Status</ColumnHead>
              </Box>
              {visible.map((outcome) => (
                <OutcomePickRow
                  key={outcome.externalId}
                  outcome={outcome}
                  selected={selectedIds.has(outcome.externalId)}
                  onReel={onReelIds.has(outcome.externalId)}
                  onSelectedChange={(next) => onOutcomeToggle(market, outcome, next)}
                />
              ))}
            </>
          )}
        </Box>
      ) : null}
    </Box>
  );
};
